import { TExistingLineQuantity } from '../contexts/types';
import GoogleAPI from '../utils/GoogleAPI';
import { isValidYearMonth } from '../utils/utils';
import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.ExistingLineQuantity.spreadsheet';

/** ヘッダー行A1の文言（PartNoは言語共通） */
const HEADER_PART_NO = "PartNo";

/** 既存品PartNoの最大行数（グラフのレイアウト保持のため。2026-10-23 打合せで決定） */
export const EXISTING_LINE_QUANTITY_MAX_ROWS = 3;

/**
 * 既存ライン生産数量データ変換結果
 */
export interface ExistingLineQuantityConversionResult {
  data: TExistingLineQuantity;
  errors: string[];
}

/**
 * セル値を "YYYY/MM" 形式に正規化する（SpreadSheetで日付に自動変換された値も受け付ける）
 * @param value セル値
 * @returns "YYYY/MM" 形式。不正な場合は空文字
 */
const normalizeYearMonth = (value: any): string => {
  // 日付に自動変換されたヘッダは、表示書式によって月・日が1桁（例: "2027/1"、"2027/1/1"）で返ることがあるため、
  // 年月を取り出して月を2桁に補ってから判定する
  const match = String(value ?? "").trim().match(/^(\d{4})[/-](\d{1,2})(?:[/-]\d{1,2})?$/);
  const ym = isValidYearMonth(match ? `${match[1]}/${match[2].padStart(2, "0")}` : value);
  return ym ? ym.replace("-", "/") : "";
};

/**
 * 既存ライン生産数量データをSpreadSheetデータに変換
 * @param data 既存ライン生産数量
 * @param labels 年月ラベル（"YYYY/MM"、量産開始〜EOL）
 * @returns SpreadSheet用の2次元配列（1行目: PartNo＋年月、2行目以降: PartNo別の月別数量）
 */
export const existingLineQuantityToSpreadSheetData = (data: TExistingLineQuantity, labels: string[]): any[][] => {
  const header = [HEADER_PART_NO, ...labels];
  // 未入力の月は空欄で出力する
  const dataRows = data.map(row => [row.partNo, ...labels.map(label => row.quantities[label] ?? "")]);
  return [header, ...dataRows];
};

/**
 * SpreadSheetデータを既存ライン生産数量データに変換
 * - PartNoが空の行はスキップ（数量が入力されている場合はエラー）
 * - 空欄は未入力（キーを省略）、数値以外・負数はエラーとしてそのセルをスキップ
 * - 表示範囲外の既存キーは保持し、範囲内のキーはSpreadSheetの値で上書きする（同じ行位置・同じPartNoの行が対象）
 * - PartNoの行数が上限（EXISTING_LINE_QUANTITY_MAX_ROWS）を超える場合はエラーとし、現在値を返す
 * @param values SpreadSheetから取得した2次元配列（ヘッダー行を含む、A1:ZZ から取得）
 * @param labels 年月ラベル（"YYYY/MM"、量産開始〜EOL）
 * @param current 現在の既存ライン生産数量（範囲外キーの保持に使用）
 * @returns 変換結果
 */
export const spreadSheetDataToExistingLineQuantity = (
  values: any[][],
  labels: string[],
  current: TExistingLineQuantity,
): ExistingLineQuantityConversionResult => {
  const errors: string[] = [];
  if (!values || values.length === 0) {
    return { data: [], errors };
  }

  // ヘッダー行の検証（A1: PartNo、B列以降: 年月ラベルの範囲内）
  const [header, ...rows] = values;
  if ((header?.[0]?.toString().trim() ?? "") !== HEADER_PART_NO) {
    errors.push(i18n.t(`${T_PREFIX}.message.invalidHeader`));
    return { data: current, errors };
  }
  const labelSet = new Set(labels);
  // 列index → 年月キー（不正・範囲外の列は null）
  const columnKeys: (string | null)[] = header.map((cell: any, col: number) => {
    if (col === 0) return null;
    const raw = cell?.toString().trim() ?? "";
    if (!raw) return null;
    const ym = normalizeYearMonth(raw);
    if (!ym || !labelSet.has(ym)) {
      errors.push(i18n.t(`${T_PREFIX}.message.invalidMonth`, { column: col + 1, value: raw }));
      return null;
    }
    return ym;
  });

  const result: TExistingLineQuantity = [];
  rows.forEach((row, i) => {
    const rowNo = i + 2;
    if (!row || row.length === 0) return;
    const partNo = row[0]?.toString().trim() ?? "";
    const hasValue = row.slice(1).some(cell => (cell?.toString().trim() ?? "") !== "");
    if (!partNo) {
      if (hasValue) {
        errors.push(i18n.t(`${T_PREFIX}.message.emptyPartNo`, { row: rowNo }));
      }
      return;
    }

    // 範囲外のキーは現在値を保持する（同じ行位置・同じPartNoの場合）
    const prev = current[result.length];
    const quantities: Record<string, number> = {};
    if (prev && prev.partNo === partNo) {
      Object.entries(prev.quantities).forEach(([key, value]) => {
        if (!labelSet.has(key)) quantities[key] = value;
      });
    }

    columnKeys.forEach((key, col) => {
      if (!key) return;
      const raw = row[col]?.toString().trim().replace(/,/g, "") ?? "";
      if (raw === "") return;
      const quantity = Number(raw);
      if (isNaN(quantity) || quantity < 0) {
        errors.push(i18n.t(`${T_PREFIX}.message.invalidQuantity`, { row: rowNo, month: key, value: row[col] }));
        return;
      }
      quantities[key] = quantity;
    });

    result.push({ partNo, quantities });
  });

  // 行数上限を超える場合は取り込まない（黙って切り捨てない）
  if (result.length > EXISTING_LINE_QUANTITY_MAX_ROWS) {
    return {
      data: current,
      errors: [i18n.t(`${T_PREFIX}.message.tooManyRows`, { max: EXISTING_LINE_QUANTITY_MAX_ROWS, count: result.length })],
    };
  }

  return { data: result, errors };
};

/**
 * 既存ライン生産数量シートの表示書式・背景色を設定
 * @param googleAPI GoogleAPIインスタンス
 * @param spreadsheetId SpreadSheet ID
 * @param sheetId シートID
 * @param monthCount 年月の列数
 */
export const setupExistingLineQuantitySheet = async (
  googleAPI: GoogleAPI,
  spreadsheetId: string,
  sheetId: number,
  monthCount: number,
): Promise<void> => {
  // 行追加に備えて50行分の書式を適用
  const FORMAT_ROW_COUNT = 50;
  const lastCol = 1 + Math.max(monthCount, 1);

  // A列: PartNoは文字列として扱う（数値風のPartNoが数値変換されないように）
  const textFormat = { numberFormat: { type: "TEXT" as const, pattern: "@" } };
  // B列以降: 数量の表示形式を桁区切りに設定
  const numberFormat = { numberFormat: { type: "NUMBER" as const, pattern: "#,##0" } };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 2, endRow: 1 + FORMAT_ROW_COUNT, startCol: 1, endCol: 1, format: textFormat },
    { sheetId, startRow: 2, endRow: 1 + FORMAT_ROW_COUNT, startCol: 2, endCol: lastCol, format: numberFormat },
  ]);

  // ヘッダー行に薄い灰色の背景色を設定
  const readOnlyBgColor = { red: 0.93, green: 0.93, blue: 0.93 };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 1, endRow: 1, startCol: 1, endCol: lastCol, format: { backgroundColor: readOnlyBgColor } },
  ]);
};
